import classNames from "classnames";
import React, { FC, useMemo } from "react";

import Button from "../../../components/button/Button";
import CustomSelect from "../../../components/select/Select";
import { TimePeriod } from "../../../hooks/useDetectTimePeriod";
import { SelectOptionType } from "../../../types/types";
import { Period, SelectDate } from "../SelectDate";

export type PaymentFilterField = "client" | "payment-date" | "label";

type Props = {
  showFilters: PaymentFilterField[];

  filter: Period;
  setFilter: React.Dispatch<React.SetStateAction<Period>>;

  filterByContact: SelectOptionType[];
  setFilterByContact: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  filterByLabel: SelectOptionType[];
  setFilterByLabel: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;

  clientOptions?: SelectOptionType[];
  labelOptions?: SelectOptionType[];

  isLoadingContacts?: boolean;

  searchClients?: string;
  setSearchClients?: (value: string) => void;

  className?: string;
  /** Billing list look (V49 design). */
  billing?: boolean;
};

export const PaymentFiltersBase: FC<Props> = ({
  showFilters,
  filter,
  setFilter,
  filterByContact,
  setFilterByContact,
  filterByLabel,
  setFilterByLabel,
  clientOptions = [],
  labelOptions = [],
  isLoadingContacts = false,
  searchClients = "",
  setSearchClients,
  className,
  billing = false,
}) => {
  const labelClass = billing
    ? "mb-1.5 text-[12.5px] font-bold normal-case text-[#3A4256]"
    : "mb-1 text-sm font-semibold normal-case text-gray-800";
  const widthClass = (billingWidth: string) =>
    billing ? billingWidth : "min-w-[200px]";
  const selectStyle = { small: true, billing };
  const isShowClear = useMemo(() => {
    return (
      !!filterByContact.length ||
      !!filterByLabel.length ||
      filter.period !== TimePeriod.All
    );
  }, [filter, filterByContact, filterByLabel]);

  return (
    <div
      className={classNames(
        "flex flex-wrap items-end",
        billing ? "gap-3.5" : "gap-4",
        className,
      )}>
      {showFilters.includes("client") && (
        <div>
          <div className={labelClass}>Client</div>
          <div className={widthClass("w-[180px]")}>
            <CustomSelect
              placeholder={"Search client"}
              value={filterByContact}
              isMulti
              inputValue={searchClients}
              options={clientOptions}
              isLoading={isLoadingContacts}
              onChangeInput={(inputValue) => setSearchClients?.(inputValue)}
              onChange={(newValue) =>
                setFilterByContact(newValue as SelectOptionType[])
              }
              selectStyle={{ ...selectStyle, countLabel: "clients" }}
            />
          </div>
        </div>
      )}

      {showFilters.includes("payment-date") && (
        <div>
          <div className={labelClass}>Payment Date</div>
          <div className={widthClass("w-[150px]")}>
            <SelectDate
              setValue={setFilter}
              value={filter}
              small
              billing={billing}
            />
          </div>
        </div>
      )}

      {showFilters.includes("label") && (
        <div>
          <div className={labelClass}>Label</div>
          <div className={widthClass("w-[160px]")}>
            <CustomSelect
              placeholder={"Search label"}
              value={filterByLabel}
              isMulti
              options={labelOptions}
              withMenu
              onChange={(newValue) =>
                setFilterByLabel(newValue as SelectOptionType[])
              }
              selectStyle={{ ...selectStyle, fitChips: true }}
            />
          </div>
        </div>
      )}

      {isShowClear && (
        <div>
          <div className={"h-6"} />
          <Button
            buttonType={"button"}
            colorType={"ghost"}
            onClick={() => {
              setFilter({
                period: TimePeriod.All,
                from: null,
                to: null,
              });
              setFilterByContact([]);
              setFilterByLabel([]);
            }}
            label={"Clear all"}
            extraClasses={"normal-case whitespace-nowrap"}
          />
        </div>
      )}
    </div>
  );
};
